import {Component, OnInit, Inject} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VitalService } from '../../configuration-management/services/vital.service';
import { VitalGroup, Order } from '../../whiteboard/models/order.interface';
import { Patient, PatientVitalGroupSummary } from 'src/app/patient-management/models/patient.interface';
import {
  FormGroup,
  FormControl,
  FormArray,
  ValidationErrors,
  AbstractControl,
  ValidatorFn
} from '@angular/forms';
import {PatientVital, PatientVitalGroup, VitalUnit} from './patient-vital-group.model';
import { PatientVitalGroupService } from './patient-vital-group.service';
import moment from 'moment';
import { Treatment } from '../../whiteboard/models/treatment.interface';
import { Whiteboard } from '../../whiteboard/models/whiteboard.interface';

export interface PatientVitalGroupDialogData {
  patient: Patient;
  patientVitalGroup: PatientVitalGroup;
  order: Order;
  hour: string;
  currentDay: string;
  orderType: string;
  whiteboard: Whiteboard;
  treatment: Treatment;
  vitalGroupType: string; // locks the vital group dialog to a specific group type
  patientVitalGroupSummary: PatientVitalGroupSummary; // for editing existing vital group summaries
}

@Component({
  selector: 'app-patient-vital-group-dialog',
  templateUrl: './patient-vital-group.dialog.html',
  styleUrls: ['./patient-vital-group.dialog.scss']
})
export class PatientVitalGroupDialogComponent implements OnInit {
  protected readonly Units = VitalUnit;

  weightVitalId = 'fae9f36c-9f9a-47f2-b0a7-aee1996912c7';

  vitalGroups: VitalGroup[];
  selectedGroup: VitalGroup;
  treatmentNotes: string;
  vitalGroupForm = new FormGroup({
    date: new FormControl(new Date()),
    id: new FormControl(null),
    patientVitals: new FormArray([])
  });
  isUpdate = false;

  constructor(
    public dialogRef: MatDialogRef<PatientVitalGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PatientVitalGroupDialogData,
    public snackBar: MatSnackBar,
    public vitalService: VitalService,
    public patientVitalGroupService: PatientVitalGroupService

  ) { }

  updateForm() {
    let existingVitals: PatientVital[] = [];
    let recordId: string = null;
    let dateValue: Date = this.getAdministeredTime();
    let isDateDisabled = false;

    if (this.data.hasOwnProperty('treatment')) {
      this.isUpdate = true;
      existingVitals = this.data.treatment.patientVitalGroup.patientVitals;
      recordId = this.data.treatment.patientVitalGroup.id;
      isDateDisabled = true;
    } else if (this.data.hasOwnProperty('patientVitalGroupSummary')) {
      this.isUpdate = true;
      existingVitals = this.data.patientVitalGroupSummary.patientVitals;
      recordId = this.data.patientVitalGroupSummary.id;
      dateValue = moment(this.data.patientVitalGroupSummary.date).toDate();
    }

    const patientVitalsArray = this.createPatientVitalsFormArray(existingVitals);

    const formConfig: any = {
      date: new FormControl({ value: dateValue, disabled: isDateDisabled }),
      vitalGroupId: new FormControl(this.selectedGroup.id),
      patientVitals: patientVitalsArray
    };

    if (recordId) {
      formConfig.id = new FormControl(recordId);
    }

    this.vitalGroupForm = new FormGroup(formConfig);
  }

  ngOnInit() {
    this.getItems();
  }

  getItems() {
    this.vitalService.indexVitalGroup().subscribe(response => {
      this.vitalGroups = response;

      if (this.data.hasOwnProperty('order')) {
        this.selectedGroup = this.vitalGroups.find(x => x.id === this.data.order.vitalGroup.id);
      } else if (this.data.hasOwnProperty('treatment')) {
        this.selectedGroup = this.vitalGroups.find(x => x.id === this.data.treatment.patientVitalGroup.vitalGroupId);
      } else if (this.data.hasOwnProperty('patientVitalGroupSummary')) {
        this.selectedGroup = this.vitalGroups.find(x => x.id === this.data.patientVitalGroupSummary.vitalGroupId);
      } else if (this.data.hasOwnProperty('vitalGroupType')) {
        this.selectedGroup = this.vitalGroups.find(x => x.name === this.data.vitalGroupType);
      }

      this.updateForm();
    });
  }

  update() {
    const model = this.vitalGroupForm.value as PatientVitalGroup;
    this.patientVitalGroupService.update(model, this.data.patient.id).subscribe(res => {
      this.data.patientVitalGroup = model;
      this.snackBar.open('Record has been updated', 'Success', {
        duration: 2000,
      });
      this.dialogRef.close(true);
    });
  }

  submit() {
    const model = this.vitalGroupForm.value as PatientVitalGroup;

    if (this.data.hasOwnProperty('order')) {
      model.whiteboardTreatment = new Treatment();
      const administeredTime = this.getAdministeredTime();
      model.whiteboardTreatment.administeredTime = administeredTime;
      model.date = administeredTime;
      model.whiteboardTreatment.orderType = this.data.orderType;
      model.whiteboardTreatment.whiteboardOrderId = this.data.order.id;
      model.whiteboardTreatment.note = this.treatmentNotes;
      model.whiteboardTreatment.patientVital = null;
      model.whiteboardTreatment.patientLabResult = null;
      model.whiteboardTreatment.whiteboardId = this.data.whiteboard.id;
    }

    this.patientVitalGroupService.create(model, this.data.patient.id).subscribe(res => {
      this.snackBar.open('Record has been created', 'Success', {
        duration: 2000,
      });
      this.dialogRef.close(true);
    });
  }

  onNoClick() {
    this.dialogRef.close();
  }

  getVitalValidators(useWeightValidation: boolean): {value: ValidatorFn[], units: ValidatorFn[]} {
    return {
      value: useWeightValidation ? [weightValueValidator] : [],
      units: useWeightValidation ? [weightUnitsValidator] : [],
    };
  }

  private createVitalFormGroup(vital: any, existingVital: any = null): FormGroup {
    const isWeightVital = vital.id === this.weightVitalId;
    const isNewVital = !existingVital || existingVital.id == null;
    const hasUnits = existingVital && existingVital.units != null;
    
    const useWeightValidation = isWeightVital && (isNewVital || hasUnits);
    const validators = this.getVitalValidators(useWeightValidation);

    const formGroup = new FormGroup({
      vitalId: new FormControl(vital.id),
      value: new FormControl(existingVital ? existingVital.value : vital.defaultValue, validators.value),
      id: new FormControl(existingVital ? existingVital.id : null),
      units: new FormControl(existingVital ? existingVital.units : null, validators.units),
    });

    if (useWeightValidation && isNewVital) {
      formGroup.patchValue({ units: VitalUnit.kilograms.name });
    }

    return formGroup;
  }

  private createPatientVitalsFormArray(existingVitals: PatientVital[] = []): FormArray {
    const formArray = new FormArray([]);
    
    this.selectedGroup.vitals.forEach(vital => {
      const existingVital = existingVitals.find(pv => pv.vitalId === vital.id);
      const formGroup = this.createVitalFormGroup(vital, existingVital);
      formArray.push(formGroup);
    });

    return formArray;
  }

  private getAdministeredTime(): Date {
    if (!this.data.currentDay) {
      return new Date();
    }

    const parsedHour = moment(this.data.hour, ['h:mm A']);
    const administeredTime = moment(this.data.currentDay);
    administeredTime.hour(parsedHour.hour());
    administeredTime.minute(0);
    administeredTime.second(0);
    administeredTime.millisecond(0);
    administeredTime.utcOffset(0);
    
    return administeredTime.toDate();
  }

  *getControlErrors(control: AbstractControl): Iterable<string> {
    const controlErrors = control.errors;
    if (controlErrors != null) {
      for (const error of Object.values(controlErrors)) {
        yield error;
      }
    }

    const parentControl = control as (FormGroup | FormArray);
    if (parentControl == null || parentControl.controls == null) {
      return;
    }

    for (const childControl of Object.values(parentControl.controls)) {
      // Yield any errors we find in child controls
      for (const error of this.getControlErrors(childControl)) {
        yield error;
      }
    }
  }
}

function weightValueValidator(control: AbstractControl): ValidationErrors | null {
  const errors: ValidationErrors = {};

  if (control.value == null || control.value === '') {
    errors.emptyValue = 'Weight value cannot be empty.';
    return errors;
  }

  const vitalValue = Number(control.value);
  if (isNaN(vitalValue)) {
    errors.notANumber = 'Weight value must be a number.';
  } else if (vitalValue <= 0) {
    errors.outOfRange = 'Weight value must be positive and non-zero.';
  }

  return errors;
}

function weightUnitsValidator(control: AbstractControl): ValidationErrors | null {
  const errors: ValidationErrors = {};
  const units = control.value;

  if (units == null || units === '') {
    errors.emptyValue = 'Weight units cannot be empty.';
  } else if (units !== 'Kilograms' && units !== 'Pounds') {
    errors.outOfRange = `'${units}' is not a valid unit of weight.`;
  }

  return errors;
}
